import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

interface IOptions {
  limit: number
  offset: number
  query: string
  sort: string
  [key: string]: number | string
}

interface IResponseList {
  status: boolean
  data: any
  count: number
}

interface IState {
  tokenData: any
  pageHeading: string
  accessToken: string
  userData: any
  loginAsBrand: boolean
  brandAccessToken: string
  brands: IResponseList
  brandOptions: IOptions
  showMessage: boolean
  message: string
  messageDuration: number
  settings: Object
  brandCreditsRemaining: number
}

function initialState () {
  let userData: any = localStorage.getItem('userData') || ''
  if (userData === '') userData = null
  else userData = JSON.parse(userData)

  return {
    tokenData: {},
    pageHeading: 'Dashboard',
    accessToken: localStorage.getItem('accessToken') || '',
    userData: userData,
    loginAsBrand: JSON.parse(localStorage.getItem('loginAsBrand') || 'false'),
    brandAccessToken: localStorage.getItem('brandAccessToken') || '',
    brands: {
      status: false,
      data: [],
      count: 0
    },
    brandOptions: {
      limit: 100,
      offset: 0,
      query: '',
      sort: 'created_at-DESC'
    },
    showMessage: false,
    message: '',
    messageDuration: 3000,
    settings: {},
    brandCreditsRemaining: 0
  }
}

export default new Vuex.Store({
  state: initialState(),
  mutations: {
    setTokenToLocalStorage (state: IState, data) {
      localStorage.setItem('tokenData', JSON.stringify(data))
      localStorage.setItem('accessToken', data.access_token)
      let tokenData = localStorage.getItem('tokenData') || ''
      let accessToken = localStorage.getItem('accessToken') || ''
      state.tokenData = JSON.parse(tokenData)
      state.accessToken = accessToken
    },

    setUserDataToLocalStorage (state: IState, data) {
      localStorage.setItem('userData', JSON.stringify(data))
      state.userData = initialState().userData
    },

    setBrandAccessToken (state: IState, data) {
      localStorage.setItem('brandAccessToken', data.access_token)
      let accessToken = localStorage.getItem('brandAccessToken') || ''
      state.brandAccessToken = accessToken
    },

    setLoginAsBrand (state: IState, data) {
      localStorage.setItem('loginAsBrand', data)
      state.loginAsBrand = data
    },

    setBrandOptions (state: IState, data: IOptions) {
      state.brandOptions = data
    },

    setBrandsList (state: IState, data) {
      for (var key in Object.keys(data.options)) {
        if (data.options[key] !== state.brandOptions[key]) {
          return
        }
      }
      // all options match
      if (data.clear) state.brands = initialState().brands
      state.brands.data.push(...data.data.data)
      state.brands.count = data.data.count
    },

    clearUserRole (state: IState) {
      state.userData.role = ''
    },

    loadUserData (state: IState) {
      state.userData = initialState().userData
    },

    showMessage (state: IState, { message, duration }) {
      state.message = message
      if (duration === undefined || duration === null) {
        state.messageDuration = initialState().messageDuration
      } else state.messageDuration = duration
      state.showMessage = true

      if (state.messageDuration > 0) {
        setTimeout(() => (state.showMessage = false), state.messageDuration)
      }
    },

    hideMessage (state: IState) {
      state.showMessage = false
      state.message = ''
    },

    showLoading (state: IState, message = null) {
      if (message) {
        state.message = message
      } else {
        state.message = 'Loading...'
      }
      state.messageDuration = 0
      state.showMessage = true
    },

    setBrandCredits (state: IState, credits) {
      state.brandCreditsRemaining = credits
    }
  },
  getters: {
    isBrandListComplete (state): boolean {
      return state.brands.count === state.brands.data.length
    }
  },
  actions: {
    getBrandsList ({ state, commit }, payload = {}) {
      return new Promise((resolve, reject) => {
        let options = {
          query:
            payload.query === undefined
              ? state.brandOptions.query
              : payload.query,
          limit:
            payload.limit === undefined
              ? state.brandOptions.limit
              : payload.limit,
          offset:
            payload.offset === undefined
              ? state.brandOptions.offset
              : payload.offset,
          sort:
          payload.sort === undefined
            ? state.brandOptions.sort
            : payload.sort,
          status:
            payload.status === undefined
              ? ''
              : payload.status
        }
        let clear = payload.clear === undefined ? true : payload.clear
        commit('setBrandOptions', options)
        Vue.http
          .get('brands?' + Vue.prototype.$common.encodeData(options), {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => {
              commit('setBrandsList', {
                options: options,
                data: response.body,
                clear: clear
              })
              resolve(response)
            },
            (response: any) => {
              // show error dialog
              reject(response)
            }
          )
      })
    },

    addBrand (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('brands', payload)
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    addEmedToUnsubscribersList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('unsubscribe-email', payload)
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    addTask (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('tasks', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    addTaskDataPoint (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + '/task_data_points', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    addBrandTutorialVideo (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + '/brand_videos_tutorials', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    editTask (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(`tasks/${payload.id}`, payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    updateTaskDataPoint (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/task_data_points/${payload.id}`, payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    updateBrandTutorialVideo (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brand_video_tutorials/${payload.id}`, payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    editBrand (context, payload) {
      return new Promise((resolve, reject) => {
        if (!payload || !payload.id || !payload.action) reject(payload)
        Vue.http.patch(`brands/${payload.id}/${payload.action}`).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => {
            reject(response)
          }
        )
      })
    },

    getBrand (context, payload: number) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${payload}?with=subscription_history+pickl_history+invoices`)
          .then(
            (response: any) => {
              let brand = response.body.data
              if (!brand.logo || brand.logo.trim().length === 0) {
                brand.logo = Vue.common.getDefaultImage('brand')
              }
              if (brand.subscription_history.length > 0) {
                let subscriptions = brand.subscription_history
                let subscriptionsFormatted: any = {}
                subscriptions.forEach((x: any) => {
                  let date = new Date(x.created_at)
                  let key = date.getFullYear() * 100 + date.getMonth()
                  if (subscriptionsFormatted[key] === undefined) {
                    subscriptionsFormatted[key] = []
                  }
                  subscriptionsFormatted[key].push(x)
                })
                brand.subscription_history = []
                for (var key in subscriptionsFormatted) {
                  brand.subscription_history.push({
                    key: key,
                    value: subscriptionsFormatted[key]
                  })
                }
                brand.subscription_history.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
              }
              if (brand.pickl_history.length > 0) {
                let ph = brand.pickl_history
                let phFormatted: any = {}
                ph.forEach((x: any) => {
                  let date = new Date(x.created_at)
                  let k = date.getFullYear() * 100 + date.getMonth()
                  if (phFormatted[k] === undefined) {
                    phFormatted[k] = []
                  }
                  phFormatted[k].push(x)
                })
                brand.pickl_history = []
                for (var k in phFormatted) {
                  brand.pickl_history.push({
                    key: k,
                    value: phFormatted[k]
                  })
                }
                brand.pickl_history.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
              }
              if (brand.invoices.length > 0) {
                let invoices = brand.invoices
                let invoicesFormatted: any = {}
                invoices.forEach((x: any) => {
                  let date = new Date(x.updated_at)
                  let invoiceKey = date.getFullYear() * 100 + date.getMonth()
                  if (invoicesFormatted[invoiceKey] === undefined) {
                    invoicesFormatted[invoiceKey] = []
                  }
                  invoicesFormatted[invoiceKey].push(x)
                })
                brand.invoices = []
                for (var invoiceKey in invoicesFormatted) {
                  brand.invoices.push({
                    key: invoiceKey,
                    value: invoicesFormatted[invoiceKey]
                  })
                }
                brand.invoices.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
              }
              response.body.data = brand
              resolve(response.body.data)
            },
            (response: any) => reject(response)
          )
      })
    },

    updatePassword ({ state }, { id, old, newp, confirm }) {
      return new Promise((resolve, reject) => {
        let payload = {
          current_password: old,
          new_password: newp,
          confirm_password: confirm
        }
        Vue.http.put(`users/${id}/password`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => {
            reject(response)
          }
        )
      })
    },

    getAppSettings (context) {
      return Vue.http.get('settings')
    },
    getRewardPoints (context) {
      return Vue.http.get('reward_points')
    },

    setAppSettings (context, payload) {
      return Vue.http.put('settings', { settings: payload })
    },

    getUserData ({ state, commit, dispatch }, userType = null) {
      return new Promise((resolve, reject) => {
        if (userType !== null) {
          Vue.http.get('token-user', {
            'headers': {
              'user-type': userType
            }
          }).then(
            (response: any) => {
              commit('setUserDataToLocalStorage', response.body)
              if (state.userData.role === 'brand' || state.userData.role === 'brand') {
                dispatch('getBrandCurrentSubscription', state.userData.brandId)
              }
              resolve(response)
            },
            (response: any) => reject(response)
          )
        } else {
          Vue.http.get('token-user').then(
            (response: any) => {
              commit('setUserDataToLocalStorage', response.body)
              if (state.userData.role === 'brand' || state.userData.role === 'brand') {
                dispatch('getBrandCurrentSubscription', state.userData.brandId)
              }
              resolve(response)
            },
            (response: any) => reject(response)
          )
        }
      })
    },

    updateProfile ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.put(`users/${payload.id}`, payload.user).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateNonGeoPicklStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-non-geo-pickl-status`, payload).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateGeoPicklStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-geo-pickl-status`, payload).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateOnBoardStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-onboard-status`, payload).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    updateBrandAgeRestrictedStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-age-restricted-status`, payload).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandSurveyFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-survey-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandDashboardDataAccess ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-dashboard-data-access`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandDemographicsFiltersFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-demographics-filters-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    updateBrandCouponFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-coupon-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandDataVisualFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-data-visual-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    updateBrandSelfieFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-selfie-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandReimbursementFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-reimbursement-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandLockPicklFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-lock-pickl-feature-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandMultiTaskFeatureStatus ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/update-multi-task-status`, payload).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    updateUserImage ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`users/${payload.id}/image`, payload.user).then(
          (response: any) => {
            dispatch('getUserData')
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    updateBrandImage ({ dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`brands/${payload.id}/image`, payload.user).then(
          (response: any) => {
            resolve(response)
          },
          (response: any) => reject(response)
        )
      })
    },

    logout ({ state, commit }) {
      return new Promise((resolve, reject) => {
        if (state.loginAsBrand) {
          Vue.notify({ type: 'error', text: 'Logout as brand first!' })
          reject(new Error('Logout as brand first!'))
          return
        }
        commit('clearUserRole')
        Vue.http.post('logout').then(
          (response: any) => {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('tokenData')
            localStorage.removeItem('userData')
            resolve(response)
          },
          (response: any) => {
            commit('loadUserData')
            reject(response)
          }
        )
      })
    },

    addFreePickls (context, payload) {
      return Vue.http.put(`brands/${payload.id}/pickls`, payload.data)
    },

    updateBrandStripeFee (context, payload) {
      return Vue.http.put(`brands/${payload.id}/stripe-fee`, payload.data)
    },
    updateBrandMaxPicklHoldLimit (context, payload) {
      return Vue.http.put(`brands/${payload.id}/max-pickl-hold-limit`, payload.data)
    },

    sendMsgToPicklrs (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('user_pickls/send_message', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    sendCustomNotificationToPicklrs (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('user_pickls/send_custom_notification', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    sendEmailToPicklrs (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('user_pickls/send_email', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    exportInsightStats (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('pickls/export_survey_report', payload, {
            headers: {
              'user-type': payload.userType
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    exportPicklInsights (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('pickls/export_pickl_report', payload, {
            headers: {
              'user-type': payload.userType
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    sendNotification (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('user_pickls/send_notification', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    sendCustomMixNotifications (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('user_pickls/send_custom_mix_notification', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },
    extendPicklExpiration (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post('pickls/extend_pickl_expiration', payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    getBrandExpiredPickls (context, payload) {
      return Vue.http.get(`brands/${payload.brandId}/pickls/expired?days=${payload.days}`)
    },

    deleteExpiredPickls (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(`brands/${payload.brandId}/pickls/expired`, payload, {
            headers: {
              'user-type': 'sadmin'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
      })
    },

    getBrandStatusCount (context, payload) {
      if (typeof payload.campaignId !== 'undefined') {
        return Vue.http.get(`brands/${payload.brandId}/pickls/status-count?campaignId=${payload.campaignId}`)
      } else {
        return Vue.http.get(`brands/${payload.brandId}/pickls/status-count`)
      }
    },

    getNonGeoPicklBrandStatusCount (context, payload) {
      return Vue.http.get(`brands/${payload}/pickls/status-count?type=NON_GEO`)
    },
    getBrandStats (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(
            `brands/${brandId}/stats`
          )
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandPickls (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(
            `brands/${payload.id}/pickls`, { params: payload }
          )
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandSubscriptionHistory ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${state.userData.brandId}/subscriptions?limit=${payload.limit}&offset=${payload.offset}&query=${payload.query}`)
          .then(
            (response: any) => {
              let currentCount = response.body.data.length
              if (currentCount > 0) {
                let subscriptions = response.body.data
                let subscriptionsFormatted: any = {}
                subscriptions.forEach((x: any) => {
                  let date = new Date(x.created_at)
                  let key = date.getFullYear() * 100 + date.getMonth()
                  if (subscriptionsFormatted[key] === undefined) {
                    subscriptionsFormatted[key] = []
                  }
                  subscriptionsFormatted[key].push(x)
                })
                subscriptions = []
                for (var key in subscriptionsFormatted) {
                  subscriptions.push({
                    key: key,
                    value: subscriptionsFormatted[key]
                  })
                }
                subscriptions.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
                response.body.data = subscriptions
              }
              resolve({
                currentCount: currentCount,
                response: response,
                options: payload
              })
            },
            (response: any) => reject(response)
          )
      })
    },

    getProductsList ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${payload.brand_id}/products?limit=${payload.limit}&offset=${payload.offset}&query=${payload.query}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getCampaigns ({ state }, payload) {
      return new Promise((resolve, reject) => {
        console.log(payload)
        Vue.http
          .get(`brands/${payload.brand_id}/pickl_campaigns`, { params: payload })
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getSadminCampaigns ({ state }, payload) {
      return new Promise((resolve, reject) => {
        console.log(payload)
        Vue.http
          .get(`pickl_campaigns`, { params: payload })
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandLoginHistory ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${payload.brand_id}/brand_login_logs?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ options: payload, data: response.data })
            },
            (response: any) => reject(response)
          )
      })
    },

    addProduct ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(`brands/${payload.brandId}/products`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    uploadMediaImages ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(
            process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/media/${payload.mediaId}/images`, payload
          )
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    uploadCouponImage ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(`coupons/${payload.couponId}/image`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    deleteProduct ({ state }, payload) {
      return Vue.http.delete(
        `brands/${payload.brandId}/products/${payload.productId}`
      )
    },

    deleteMedia ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/media/${payload.mediaId}`, payload
      )
    },
    deleteSurveyQuestion ({ state }, payload) {
      return Vue.http.delete(
        `brands/${payload.brandId}/surveys/${payload.urveyId}/questions/${payload.questionId}`
      )
    },

    getStores (context, payload: any) {
      return new Promise((resolve, reject) => {
        Vue.http.get('stores', { params: payload }).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getBrands (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands?limit=${payload.limit}&offset=${payload.offset}&sort=${payload.sort}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getCountriesList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`countries?query=${payload.query}&limit=${payload.limit}&offset=${payload.offset}&service_enabled=${payload.service_enabled}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getOffersList (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/offers?query=${payload.query}&limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getTaskDataPoints (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/task_data_points`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandTutorialVideos (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brand_video_tutorials?query=${payload.query}&limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandOffersList (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/offers`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getOffer (content, offerId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/offers/${offerId}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getTasksList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`tasks?query=${payload.query}&limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandProducts (context, payload) {
      let brandId = payload.brandId
      delete payload['brandId']
      return new Promise((resolve, reject) => {
        Vue.http.get(`brands/${brandId}/products`, { params: payload }).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getTasks (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`tasks?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getAppSettingsKeyValuePair (context) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`settings-key-values`).then(
          (response: any) => {
            context.state.settings = response.body.data
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    addPickl (context: any, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(`brands/${payload.brandId}/pickls`, payload.pickl, {
            headers: {
              'user-type': 'brand'
            }
          })
          .then(
            (response: any) => resolve(response),
            (response: any) => reject(response)
          )
          .finally(() =>
            context.dispatch(
              'getBrandCurrentSubscription',
              payload.brandId
            )
          )
      })
    },
    loginAsBrand ({ commit, dispatch }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.post(`brands/${payload.brandId}/login`).then(
          (response: any) => {
            commit('setBrandAccessToken', response.body)
            commit('setLoginAsBrand', true)
            dispatch('getUserData').then(
              (user: any) => resolve(user),
              (user: any) => reject(user)
            )
          },
          (response: any) => reject(response)
        )
      })
    },
    logoutAsBrand ({ state, commit, dispatch }) {
      if (state.loginAsBrand) {
        return new Promise((resolve, reject) => {
          commit('clearUserRole')
          Vue.http.post('logout').then(
            (response: any) => {
              localStorage.removeItem('brandAccessToken')
              localStorage.removeItem('userData')
              commit('setBrandAccessToken', { accessToken: '' })
              commit('setLoginAsBrand', false)
              dispatch('getUserData').then(
                (user: any) => resolve(user),
                (user: any) => reject(user)
              )
            },
            (response: any) => {
              commit('loadUserData')
              reject(response)
            }
          )
        })
      } else {
        return dispatch('logout')
      }
    },
    getBrandProfile (_context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`brands/${brandId}/profile`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getNonGeoUserPicklDetails (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`user_pickls/non-geo/${payload.userPicklId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getUserPicklDetails (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`user_pickls/geo/${payload.userPicklId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    updateBrandProfile (_context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.put(`brands/${payload.id}`, payload).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    editPicklPack (_context, payload) {
      return Vue.http.put(`subscriptions/${payload.id}`, payload.data)
    },
    resetLocalStorage ({ commit }) {
      return new Promise((resolve, reject) => {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('tokenData')
        localStorage.removeItem('brandAccessToken')
        localStorage.removeItem('loginAsBrand')
        localStorage.removeItem('userData')
        commit('loadUserData')
        resolve()
      })
    },
    getBrandCurrentSubscription ({ commit }, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`brands/` + brandId + `/current_subscription`).then(
          (response: any) => {
            if (
              response.body.data &&
              response.body.data.credits !== undefined &&
              response.body.data.credits !== null
            ) {
              commit('setBrandCredits', response.body.data.credits)
            }
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    saveClientLog (_context, payload) {
      return Vue.http.post('web-app-errors', payload)
    },
    getProductSections (context, payload: any) {
      return new Promise((resolve, reject) => {
        Vue.http.get('product_sections', { params: payload }).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getStoreLocations (context, payload: any) {
      return new Promise((resolve, reject) => {
        Vue.http.get('store_locations', { params: payload }).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getBrandPicklHistory ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${state.userData.brandId}/pickl_history?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              let currentCount = response.body.data.length
              if (currentCount > 0) {
                let subscriptions = response.body.data
                let subscriptionsFormatted: any = {}
                subscriptions.forEach((x: any) => {
                  let date = new Date(x.created_at)
                  let key = date.getFullYear() * 100 + date.getMonth()
                  if (subscriptionsFormatted[key] === undefined) {
                    subscriptionsFormatted[key] = []
                  }
                  subscriptionsFormatted[key].push(x)
                })
                subscriptions = []
                for (var key in subscriptionsFormatted) {
                  subscriptions.push({
                    key: key,
                    value: subscriptionsFormatted[key]
                  })
                }
                subscriptions.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
                response.body.data = subscriptions
              }
              resolve({
                currentCount: currentCount,
                response: response,
                options: payload
              })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandReimbursementCreditsLog ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${payload.brandId}/reimbursement-credits-log?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandReimbursementCreditsPurchaseLog ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${payload.brandId}/reimbursement-credits-purchase-log?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    updateProduct ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(
            `brands/${state.userData.brandId}/products/${payload.id}`,
            payload
          )
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getSubscriptionHistory (_context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get('invoices', { params: payload }).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    restoreLocation (_context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`store_locations/${payload.id}/restore`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    toggleCountryStatus (_context, country) {
      return new Promise((resolve, reject) => {
        Vue.http.put(`countries/${country.id}`, country).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    restoreStore (_context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.patch(`stores/${payload.id}/restore`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getBrandInvoiceHistory ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(`brands/${state.userData.brandId}/invoices?limit=${payload.limit}&offset=${payload.offset}&query=${payload.query}`)
          .then(
            (response: any) => {
              let currentCount = response.body.data.length
              if (currentCount > 0) {
                let subscriptions = response.body.data
                let subscriptionsFormatted: any = {}
                subscriptions.forEach((x: any) => {
                  let date = new Date(x.created_at)
                  let key = date.getFullYear() * 100 + date.getMonth()
                  if (subscriptionsFormatted[key] === undefined) {
                    subscriptionsFormatted[key] = []
                  }
                  subscriptionsFormatted[key].push(x)
                })
                subscriptions = []
                for (var key in subscriptionsFormatted) {
                  subscriptions.push({
                    key: key,
                    value: subscriptionsFormatted[key]
                  })
                }
                subscriptions.sort((a: any, b: any) => {
                  return b['key'] - a['key']
                })
                response.body.data = subscriptions
              }
              resolve({
                currentCount: currentCount,
                response: response,
                options: payload
              })
            },
            (response: any) => reject(response)
          )
      })
    },
    getProductImage (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`product_image/${payload.productId}/${payload.imageId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getSurveysList ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brand_id}/surveys?size=${payload.limit}&offset=${payload.offset}&query=${payload.query}&type=${payload.type}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandCoupons ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/coupons/brand?limit=${payload.limit}&offset=${payload.offset}&search=${payload.query}&type=PICKL&brandId=${payload.brandId}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getGlobalCoupons ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/coupons/global?size=${payload.limit}&offset=${payload.offset}&query=${payload.query}&type=${payload.type}&brandId=${payload.brand_id}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getMediaList ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brand_id}/media?limit=${payload.limit}&offset=${payload.offset}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getLocationCatalogueList ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brand_id}/location_catalogues?size=${payload.limit}&offset=${payload.offset}&query=${payload.query}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getClusterLocations ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brand_id}/location_catalogue/store_locations?type=${payload.type}&location_catalogue_ids=${payload.ids}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    createStoreCluster ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogue/create`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getSurveyById ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getCouponById ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/coupons/${payload.couponId}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getLocationCatalogueById ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues/${payload.locationCatalogueId}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },

    getCatalogueStoreLocations ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues/${payload.locationCatalogueId}/locations`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getCouponStates ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/coupon_states/${payload.couponId}`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    getStateCities ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/states/${payload.stateId}/cities`)
          .then(
            (response: any) => {
              resolve({ options: payload, response: response })
            },
            (response: any) => reject(response)
          )
      })
    },
    saveSurvey ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    saveMedia ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/media`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    saveCoupon ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/coupons`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    saveBulkCouponStates ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/coupon_states/bulk`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateCoupon ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/coupons/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateMedia ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/media/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    saveLocationCatalogue ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    upadtedLocationCatalogue ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brand_id}/location_catalogues/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateSurvey ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    saveQuestion ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}/questions`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    updateQuestion ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}/questions/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    saveAnswer ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}/questions/${payload.questionId}/answers`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    updateAnswer ({ state }, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}/questions/${payload.questionId}/answers/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    deleteSurvey ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/surveys/${payload.surveyId}`, payload
      )
    },

    deleteCoupon ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/coupons/${payload.couponId}`, payload
      )
    },
    deleteCouponState ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/coupon_states/${payload.id}`, payload
      )
    },

    deleteLocationCatalogue ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues/${payload.catalogueId}`, payload
      )
    },
    deleteCatalogueStoreLocation ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues/${payload.locationCatalogueId}/locations/${payload.catalogueStoreLocationId}`, payload
      )
    },

    deleteOffer ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/offers/${payload.offerId}`, payload
      )
    },

    deleteBrandOffer ({ state }, payload) {
      return Vue.http.delete(
        process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/offers/${payload.brandOfferId}`, payload
      )
    },

    getPicklDetails (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`pickls/${payload.picklId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },

    getNonGeoPicklDetails (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`pickls/non-geo/${payload.picklId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    getMultipleResponsePicklDetails (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.get(`pickls/multiple-responses/${payload.picklId}`).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },

    cancelBrandSubscription (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.post(`brands/${payload.brandId}/subscription/cancel`, payload).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    addLoginLog (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http.post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/login-log`, payload).then(
          (response: any) => {
            resolve({ response: response })
          },
          (response: any) => reject(response)
        )
      })
    },
    saveOffer (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/offers`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    craeteOfferLink (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API2_BASE_URL + `/offers/${payload.offerId}/link`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    bulkAssignOfferBrands (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/offers/${payload.offerId}/brands`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    bulkAssignLocationCatalogueStoreLocations (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/location_catalogues/${payload.locationCatalogueId}/locations`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateOffer (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/offers/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    assignOfferToBrand (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/offers`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    fetchOfferBySecret (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API2_BASE_URL + `/brands/${payload.brandId}/offers/link`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    sendInvitationLilnk (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/invitations/brands/${payload.brandId}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateInvitationStatus (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/invitations/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandInvitationsList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/invitations/brands/${payload.brandId}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getEmailInvitationsList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/invitations/email/${payload.email}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandUsersList (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/users`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandRoles () {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/roles/brand`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    verifyInvitaionSecret (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/invitations/secret/verify`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    reLaunchExpiredTasks (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/pickls/relaunch`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    deleteBrandInvitation (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .delete(process.env.VUE_APP_API_BASE_URL + `/invitations/${payload.invitationId}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    removeUserBrand (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .delete(process.env.VUE_APP_API_BASE_URL + `/user-brands/${payload.id}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getUserBrands (context, userId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/users/${userId}/brands`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    switchUserBrand (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/users/${payload.userId}/brands/${payload.brandId}/switch`, payload, {
            headers: {
              'user-type': 'brand'
            }
          })
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    switchUserBrandDashboard (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/users/${payload.userId}/brands/${payload.brandId}/switch-dashboard`, payload, {
            headers: {
              'user-type': 'brand'
            }
          })
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandPicklCompetitions (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_ML_BASE_URL + `/competitions?imgSource=` + process.env.VUE_APP_GS_PATH + `${payload.userPicklId}/${payload.image}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    saveNonGeoPickl (context, payload:any) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/pickls/create-non-geo-pickl`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
          .finally(() =>
            context.dispatch(
              'getBrandCurrentSubscription',
              payload.brandId
            )
          )
      })
    },
    saveUserPicklComment (context, payload: any) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/user_pickl_comments`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getUserPicklCommentsByUserPicklId (context, userPicklId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/user_pickl_comments/${userPicklId}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    updateUserPickl (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/users/${payload.userId}/pickls/${payload.id}`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    popPickls (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/pickls/locations/pop`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    relaunchPickls (content, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/pickls/relaunch`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    exportPickls (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/pickls/export`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    // brand report insights

    getBrandProductInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/productwiselaunches`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandPicklTypeInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/pickltypeinsights`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandStateInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/statewiselaunches`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    getBrandsurveyInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/surveyinsights`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandRetailerInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/retailerwiselaunches`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandShoppingInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/shoppinginsights`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandPicklInsights (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/reports/picklsinsights?view=${payload.viewby}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandAverageCompletionTimes (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/reports/getaveragecompletiontimes?type=${payload.type}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandIssuesInsights (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/getissuesinsights`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandSocialSelfies (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/reports/userselfies`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getCampaignCalendarData (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/pickl_campaigns/calendar-data?brandId=${brandId}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getNonGeoPicklCampaignCalendarData (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/pickl_campaigns/non-geo-calendar-data`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    sendOtpToVerifyAccountRemoval (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/remove-account-otp`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    verifyOtpAndRemoveAccount (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/verify-otp-to-remove-account`, payload)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandImages (context, payload) {
      console.log(payload)
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/pickl-images/${payload.brandId}`, { params: payload })
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandUsedTasks (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/pickl-tasks-list/${payload.brandId}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getBrandUsedStores (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/pickl-store-list/${payload.brandId}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },
    getStates (context) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/states?country_id=234`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    // Brand theme colors functionality
    getBrandThemeColors (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/theme-colors`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateBrandThemeColors (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/theme-colors`, payload.colors)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    // Brand settings functionality
    getBrandSettings (context, brandId) {
      return new Promise((resolve, reject) => {
        Vue.http
          .get(process.env.VUE_APP_API_BASE_URL + `/brands/${brandId}/settings`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    addBrandSetting (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .post(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/settings`, payload.setting)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    updateBrandSetting (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .put(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/settings/${payload.settingId}`, payload.setting)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    },

    deleteBrandSetting (context, payload) {
      return new Promise((resolve, reject) => {
        Vue.http
          .delete(process.env.VUE_APP_API_BASE_URL + `/brands/${payload.brandId}/settings/${payload.settingId}`)
          .then(
            (response: any) => {
              resolve(response)
            },
            (response: any) => reject(response)
          )
      })
    }
  }
})
